import type {Bullets, Fields, FontWeights, SectionData} from '../gutenberg/blocks/widget-styles/Section';
import type {Attr as NavigationAttr, ServerSideRenderRequired, WithCommon} from '../gutenberg/blocks/navigation/block';
import type {Attr as PagesAttr} from '../gutenberg/blocks/pages/Edit';
import type {Attr as CategoryAttr} from '../gutenberg/blocks/categories/Edit';
import type {ComponentClass, ReactNode, SVGProps} from 'react';
import type {BlockAttributes, BlockSupports, CreateBlock} from '@wordpress/blocks';
import type {AccordionsEnabled, SvgInHtml} from '../accordions';
import type {LegacyObject} from '../accordions/legacy-asm-pro';
import type {IconType} from '../gutenberg/blocks/Accordion';
import type {PostTypeOptions} from '../gutenberg/blocks/categories/DisplayPosts';

type AccordionStyle = {
	contract: string;
	expand: string;
	label: string;
}

// @see content/plugins/advanced-sidebar-menu/js/src/gutenberg/index.ts
type PassedGlobals = {
	ErrorBoundary: ComponentClass<{
		attributes: object;
		block: string;
		section: string;
		children?: ReactNode;
	}>;
	getBlockSupports: () => BlockSupports;
	NavigationIcon: SVGProps<SVGSVGElement>;
	Preview: ( {attributes, block, clientId}: {
		attributes: PagesAttr | CategoryAttr | NavigationAttr;
		block: string;
		clientId: string;
	} ) => ReactNode;
	transformLegacyWidget: ( name: string ) => ( widgetValues: {
		instance: { [ key: string ]: string | number | object | boolean }
	} ) => CreateBlock<WithCommon<NavigationAttr>>;
	translateBlockAttributes: ( attr: BlockAttributes<NavigationAttr> ) => BlockAttributes<WithCommon<NavigationAttr>>;
}


export type WPBoolean = '1' | '';

export type Screen = 'site-editor' | 'widgets' | 'post' | 'customize';

export interface JSConfig {
	blocks: {
		commonAttr: BlockAttributes<WithCommon<Record<string, never>>>;
		previewAttr: BlockAttributes<ServerSideRenderRequired>;
		blockSupport: BlockSupports;
		categories: {
			id: 'advanced-sidebar-menu/categories';
			attributes: BlockAttributes<CategoryAttr>;
		};
		pages: {
			id: 'advanced-sidebar-menu/pages';
			attributes: BlockAttributes<PagesAttr>;
		};
		navigation: {
			id: 'advanced-sidebar-menu/navigation';
			attributes: BlockAttributes<NavigationAttr>;
		}
	};
	categories: {
		excluded: number[];
		displayEach: {
			list: string;
			widget: string;
		};
		order: {
			ASC: string;
			DESC: string;
		};
		orderBy: { [ value: string ]: string }
	};
	displayPosts: {
		options: { [ value: string ]: string };
		orderBy: { [ value: string ]: string };
		types: PostTypeOptions;
	};
	docs: {
		page: string;
		category: string;
		navigation: string;
	};
	fields: {
		title: 'advanced-sidebar-menu/link-title';
		excludePage: 'advanced-sidebar-menu/exclude-page';
	};
	error: string;
	features: string[];
	isPostEdit: WPBoolean;
	isPro: '1';
	isWidgets: WPBoolean;
	menus: { [ value: number ]: string };
	pages: {
		excluded: number[];
		orderBy: { [ value: string ]: string };
		order: {
			ASC: string;
			DESC: string;
		};
	};
	postTypes: { [ value: string ]: string };
	currentScreen: Screen;
	siteInfo: {
		basic: string;
		classicWidgets: boolean;
		menus: Array<object>;
		php: string;
		pro: string | false;
		scriptDebug: boolean;
		WordPress: string;
	};
	styles: {
		bullets: Bullets;
		fields: Fields;
		sections: SectionData[];
		weights: FontWeights;
	};
	support: string;
	taxonomies: { [ value: string ]: string };
}

declare global {
	interface Window {
		ADVANCED_SIDEBAR_MENU: PassedGlobals & JSConfig;
		ADVANCED_SIDEBAR_MENU_ACCORDIONS: {
			iconStyle: 'accordion_style';
			icons: Record<IconType, AccordionStyle>;
		};
		advancedSidebarMenuPro?: {
			accordions: {
				enabled: AccordionsEnabled;
				expandSubMenu: ( $menuItem: JQuery<HTMLLIElement>, $icon: JQuery<SvgInHtml> ) => void;
				init: ( force?: boolean ) => void;
				removeAccordion: ( widgetId: string ) => void;
			};
		};
		asm_pro?: LegacyObject;
		elementorFrontend?: {
			hooks: {
				addAction: ( action: string, callback: () => void ) => void;
			}
		};
		__TEST__?: boolean;
	}
}

export const CONFIG: PassedGlobals & JSConfig = window.ADVANCED_SIDEBAR_MENU;
